import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Accounts from './pages/Accounts';
import Debts from './pages/Debts';
import Transactions from './pages/Transactions';
import Subscriptions from './pages/Subscriptions';
import Transfers from './pages/Transfers';
import Settings from './pages/Settings';
import Admin from './pages/Admin';
import Imprint from './pages/Imprint';
import Privacy from './pages/Privacy';
import Navigation from './components/Navigation';
import api from './utils/api';
import { usePageTracking } from './hooks/usePageTracking';

// Hilfsfunktion zur Token-Validierung
const isTokenExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const expiration = payload.exp * 1000; // JWT exp ist in Sekunden
    return Date.now() >= expiration;
  } catch (error) {
    return true; // Ungültiges Token = abgelaufen
  }
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  // Token-Validierung beim Start
  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('accessToken');
      if (token && !isTokenExpired(token)) {
        setIsAuthenticated(true);
      } else {
        // Token fehlt oder abgelaufen (Refresh passiert automatisch via Interceptor)
        setIsAuthenticated(!!token);
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const handleLogin = () => {
    setIsAuthenticated(true);
  };

  const handleLogout = async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    
    // Backend-Logout durchführen
    if (refreshToken) {
      try {
        await api.post('/auth/logout', { refreshToken });
      } catch (error) {
        console.error('Logout-Fehler:', error);
      }
    }
    
    localStorage.clear();
    setIsAuthenticated(false);
  };

  if (loading) {
    return <div className="loading">Lädt...</div>;
  }

  return (
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true
      }}
    >
      <PageTracker />
      {isAuthenticated && <Navigation onLogout={handleLogout} />}
      <Routes>
        <Route
          path="/login"
          element={
            !isAuthenticated ? (
              <Login onLogin={handleLogin} />
            ) : (
              <Navigate to="/dashboard" replace />
            )
          }
        />
        <Route
          path="/register"
          element={
            !isAuthenticated ? (
              <Register onRegister={handleLogin} />
            ) : (
              <Navigate to="/dashboard" replace />
            )
          }
        />
        <Route
          path="/dashboard"
          element={
            isAuthenticated ? <Dashboard /> : <Navigate to="/login" replace />
          }
        />
        <Route
          path="/accounts"
          element={
            isAuthenticated ? <Accounts /> : <Navigate to="/login" replace />
          }
        />
        <Route
          path="/debts"
          element={
            isAuthenticated ? <Debts /> : <Navigate to="/login" replace />
          }
        />
        <Route
          path="/subscriptions"
          element={
            isAuthenticated ? <Subscriptions /> : <Navigate to="/login" replace />
          }
        />
        <Route
          path="/transfers"
          element={
            isAuthenticated ? <Transfers /> : <Navigate to="/login" replace />
          }
        />
        <Route
          path="/transactions"
          element={
            isAuthenticated ? <Transactions /> : <Navigate to="/login" replace />
          }
        />
        <Route
          path="/settings"
          element={
            isAuthenticated ? <Settings /> : <Navigate to="/login" replace />
          }
        />
        <Route
          path="/admin"
          element={
            isAuthenticated ? <Admin /> : <Navigate to="/login" replace />
          }
        />
        <Route path="/imprint" element={<Imprint />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route
          path="/"
          element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Landing />}
        />
      </Routes>
    </BrowserRouter>
  );
}

// Tracking-Component (innerhalb von BrowserRouter)
const PageTracker: React.FC = () => {
  usePageTracking();
  return null;
};

export default App;
